// ============================================
// HISTORY - Change history display
// ============================================

import { HistoryEntry, SHIFT_FULL_NAMES } from './types.js';
import { fetchHistory } from './api.js';
import { formatDateShort } from './shifts-logic.js';
import { isAuthenticated } from './auth.js';

/**
 * Format timestamp to readable format
 */
function formatTimestamp(isoTimestamp: string): string {
  const date = new Date(isoTimestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  // Relative time for recent changes
  if (diffMins < 1) return 'только что';
  if (diffMins < 60) return `${diffMins} мин назад`;
  if (diffHours < 24) return `${diffHours} ч назад`;
  if (diffDays < 7) return `${diffDays} д назад`;

  // Absolute time for older changes
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const mins = date.getMinutes().toString().padStart(2, '0');

  return `${day}.${month}.${year} ${hours}:${mins}`;
}

/**
 * Get Russian shift name
 */
function getShiftName(shift: string): string {
  const shiftMap: Record<string, string> = {
    earlyMorning: 'Ранее утро',
    morning: 'Утро',
    evening: 'Вечер',
    night: 'Ночь'
  };
  return shiftMap[shift] || shift;
}

/**
 * Get person name in Russian
 */
function getPersonName(person: 'alena' | 'ilya'): string {
  return person === 'alena' ? 'Алёна' : 'Илья';
}

/**
 * Format change description
 */
function formatChangeDescription(entry: HistoryEntry): string {
  if (entry.changeType === 'kindergarten') {
    const action = entry.newValue ? 'включил' : 'выключил';
    return `${action} садик на ${formatDateShort(entry.date)}`;
  }

  if (entry.changeType === 'shift' && entry.person && entry.shift) {
    const action = entry.newValue ? 'добавил' : 'убрал';
    const person = getPersonName(entry.person);
    const shift = getShiftName(entry.shift);
    return `${action} смену "${shift}" для ${person} на ${formatDateShort(entry.date)}`;
  }

  return 'неизвестное изменение';
}

/**
 * Render history entry
 */
function renderHistoryEntry(entry: HistoryEntry): HTMLElement {
  const item = document.createElement('div');
  item.className = 'history-item';

  const header = document.createElement('div');
  header.className = 'history-header';

  const user = document.createElement('span');
  user.className = 'history-user';
  user.textContent = entry.username;

  const time = document.createElement('span');
  time.className = 'history-time';
  time.textContent = formatTimestamp(entry.timestamp);

  header.appendChild(user);
  header.appendChild(time);

  const description = document.createElement('div');
  description.className = 'history-description';
  description.textContent = formatChangeDescription(entry);

  item.appendChild(header);
  item.appendChild(description);

  return item;
}

/**
 * Render history list
 */
async function renderHistory(): Promise<void> {
  const container = document.getElementById('history-container');
  if (!container) return;

  try {
    container.innerHTML = '<div class="loading">Загрузка истории...</div>';

    const history = await fetchHistory();

    if (history.length === 0) {
      container.innerHTML = '<div class="empty-state">История изменений пуста</div>';
      return;
    }

    container.innerHTML = '';

    history.slice(0, 200).forEach(entry => {
      container.appendChild(renderHistoryEntry(entry));
    });
  } catch (e) {
    console.error('Failed to load history:', e);
    container.innerHTML = '<div class="error">Не удалось загрузить историю. Проверьте подключение к серверу.</div>';
  }
}

/**
 * Initialize history page
 */
async function initHistory(): Promise<void> {
  if (!isAuthenticated()) {
    window.location.href = 'index.html';
    return;
  }
  await renderHistory();
}

// Initialize on DOM load
document.addEventListener('DOMContentLoaded', initHistory);
